import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { navigate, setFilters } from '../router.js';
import { since, longDate, count } from '../lib/format.js';

const SOURCE_LABEL = {
  workamajig: 'Workamajig',
  hubspot: 'HubSpot',
  google_calendar: 'Master Calendar',
};

/**
 * The run and freshness banner (PRD 12.1, TRD 11): which forecast run every number on screen
 * comes from, and when each source last delivered the records behind it. A stale source or a
 * missing run is stated plainly — a planner should never have to wonder whether what they are
 * looking at is this morning's or last Tuesday's.
 *
 * Since Phase 35 that includes the largest thing the numbers can be: a scenario. When one is
 * selected the banner turns and says so in the first position, with the way back beside it,
 * because every chart below is then answering a question somebody made up rather than reporting
 * the portfolio, and nothing else on the screen says which.
 */
class RunBanner extends StoreMixin(LitElement) {
  static styles = css`
    /* Printing is this tool's image export (Phase 37): which run is showing belongs in the printed heading, not in a banner. */
    @media print {
      :host {
        display: none !important;
      }
    }
    :host {
      display: block;
      background: var(--surface);
      border-bottom: 1px solid var(--border-soft);
    }
    .row {
      display: flex;
      align-items: baseline;
      gap: 8px 22px;
      flex-wrap: wrap;
      padding: 8px var(--page-gutter);
      font-size: 12.5px;
      color: var(--ink-muted);
    }
    .run {
      color: var(--ink);
      font-weight: 600;
    }
    .scenario {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 2px 10px;
      border-radius: 999px;
      background: var(--link);
      color: var(--surface);
      font-weight: 600;
    }
    .scenario .stale {
      font-weight: 400;
      opacity: 0.85;
    }
    .scenario button {
      font: inherit;
      font-size: 11.5px;
      font-weight: 600;
      color: var(--surface);
      background: none;
      border: 1px solid var(--surface);
      border-radius: 999px;
      padding: 0 8px;
      cursor: pointer;
    }
    .run .horizon {
      font-weight: 400;
      color: var(--ink-muted);
    }
    .sources {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
      margin-left: auto;
    }
    .source {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      white-space: nowrap;
    }
    /* The dot is never the only signal: the source name and the age are always spelled out. */
    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--status-healthy);
      flex: none;
    }
    .dot.stale {
      background: var(--status-caution);
    }
    .dot.never {
      background: var(--status-overloaded);
    }
    .dot.running {
      background: var(--type-1);
    }
    .warn {
      color: var(--ink);
      font-weight: 600;
    }
    time {
      font-variant-numeric: tabular-nums;
    }
    @media (max-width: 900px) {
      .sources {
        margin-left: 0;
      }
    }
  `;

  _source(source) {
    const label = SOURCE_LABEL[source.source_system] ?? source.source_system;
    const state = source.running
      ? 'running'
      : !source.last_success_at
        ? 'never'
        : source.stale
          ? 'stale'
          : 'fresh';
    const age = source.running
      ? 'syncing now'
      : source.last_success_at
        ? since(source.last_success_at)
        : 'never synced';
    return html`
      <span
        class="source"
        title=${
          source.last_success_at
            ? `${label} last succeeded ${new Date(source.last_success_at).toLocaleString()}`
            : `${label} has never completed a sync`
        }
      >
        <span class="dot ${state}"></span>
        <span>${label}</span>
        <time class=${state === 'fresh' || state === 'running' ? '' : 'warn'}>${age}</time>
      </span>
    `;
  }

  render() {
    const { run, sources, settings, filters, scenarios } = this.appState;
    const scenario = filters?.scenario
      ? ((scenarios ?? []).find((s) => s.id === filters.scenario) ?? { name: 'A scenario' })
      : null;
    return html`
      <div class="row">
        ${
          scenario
            ? html`
                <span class="scenario">
                  Scenario: ${scenario.name}
                  ${
                    scenario.stale
                      ? html`<span class="stale">· assumptions changed since it was computed</span>`
                      : nothing
                  }
                  <button
                    @click=${() => {
                      setFilters({ scenario: null });
                    }}
                  >
                    Back to the base forecast
                  </button>
                  <button @click=${() => navigate('/scenarios')}>Scenarios</button>
                </span>
              `
            : nothing
        }
        ${
          run
            ? html`
                <span class="run">
                  Forecast run ${since(run.generated_at)}
                  <span class="horizon">
                    · planning week of ${longDate(run.horizon_start)} · ${run.horizon_weeks} weeks ·
                    ${count(run.allocation_count)} allocation rows · pipeline ${run.pipeline_mode}
                  </span>
                </span>
              `
            : html`<span class="run warn">No forecast run yet — views will be empty.</span>`
        }
        ${settings ? html`<span>${settings.planning.timezone}</span>` : ''}
        <span class="sources">${(sources ?? []).map((s) => this._source(s))}</span>
      </div>
    `;
  }
}

customElements.define('run-banner', RunBanner);
